import React from 'react';
import { ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';

import { ExtensionKey } from 'flow/types/EntityKeys';
import AccessCandidateItem from './AccessCandidateItem';

/**
 * Anchored under the field and portalled out of the dialog, so it neither pushes
 * the sections below out of reach nor gets clipped by the dialog's own overflow.
 * It flips above the field by itself when there is no room under it.
 *
 * The height cap has to be the anchor variable rather than a max-h class:
 * anchoring writes an inline `max-height: min(var(--anchor-max-height), <space
 * below>)`, and an inline style wins against a class.
 *
 * The stable gutter keeps the thumb off the names rather than over them.
 */
export const ACCESS_CANDIDATE_PANEL_CLASSES =
    'scrollbar scrollbar-contrast scrollbar-gutter-stable z-50 w-(--input-width) rounded-lg bg-white py-2 shadow-lg [--anchor-max-height:18rem] focus:outline-hidden dark:bg-gray-300';

interface OptionProps extends React.HTMLAttributes<HTMLDivElement> {
    option: ExtensionKey;
}

/**
 * One row, as the virtualiser hands it over.
 *
 * Everything but `option` is passed straight through, and has to be: the
 * virtualiser clones this row to place it, so the absolute positioning and the
 * index attributes arrive as props. A component that keeps them to itself leaves
 * every row in normal flow inside a spacer thousands of pixels tall, which empties
 * the list as soon as it scrolls.
 *
 * Full width for the same reason — the row is positioned with no width of its own,
 * so it, and with it the hover, would otherwise stop at the end of the name.
 */
const AccessCandidateOption = ({ option, ...positioning }: OptionProps) => (
    <ComboboxOption
        {...positioning}
        value={option}
        className={({ focus }) =>
            twMerge('w-full cursor-pointer', focus && 'darken-10')
        }
    >
        <AccessCandidateItem extensionKey={option} />
    </ComboboxOption>
);

/**
 * Rows come from the combobox's virtualiser, so a customer of several hundred
 * colleagues mounts a screenful rather than all of them — each row subscribes to
 * the store for its name and avatar, which is what made opening the list drag.
 */
const AccessCandidateList = () => (
    <ComboboxOptions
        anchor={{ to: 'bottom start', gap: 4 }}
        modal={false}
        // Not modal: Headless UI otherwise marks the rest of the dialog
        // inert while the list is open, which took the members list, the tier
        // select and Done out of reach — a click on a member's remove button
        // did nothing at all. The list still closes on an outside click.
        className={ACCESS_CANDIDATE_PANEL_CLASSES}
    >
        {(slot: { option: ExtensionKey }) => (
            <AccessCandidateOption option={slot.option} />
        )}
    </ComboboxOptions>
);

export default AccessCandidateList;
